import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/button';
import { 
  LogOut, 
  Menu, 
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
  ScanLine,
  Check,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UserProfileImage from '../KanbanBoard/UserProfileImage';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandGroup, CommandItem, CommandList } from '../ui/command';
import { cn } from '@/lib/utils';
import NotificationDropdown from './NotificationDropdown';

interface SearchField {
  id: string;
  label: string;
  placeholder: string;
}

export enum TopbarBackgroundStyle {
  DEFAULT = 'default',
  BLUE_GRADIENT = 'blue-gradient',
  BLUE = 'blue',
  YELLOW = 'yellow',
  GREEN = 'green',
  RED = 'red',
  DARK = 'dark',
  TRANSPARENT = 'transparent',
  WHITE = 'white'
}

interface EnhancedModernTopbarProps {
  title?: string;
  showMenuButton?: boolean;
  onMenuClick?: () => void;
  showCollapseButton?: boolean;
  onCollapseClick?: () => void;
  isSidebarCollapsed?: boolean;
  // Background styling
  backgroundStyle?: TopbarBackgroundStyle;
  // Search functionality
  enableSearch?: boolean;
  searchFields?: SearchField[];
  onSearch?: (query: string, field: string) => void;
  onSearchFieldChange?: (field: string) => void;
  initialSearchQuery?: string;
  initialSearchField?: string;
  enableScan?: boolean;
  // Enable right section including actions and user menu
  enableUserLogoutSection?: boolean;
  // Action buttons
  primaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  children?: React.ReactNode;
}

// Default search field - defined outside component to avoid recreating
const DEFAULT_SEARCH_FIELD: SearchField = { 
  id: 'all', 
  label: 'ทั้งหมด', 
  placeholder: 'ค้นหา...' 
};

const EnhancedModernTopbar: React.FC<EnhancedModernTopbarProps> = ({ 
  title,
  showMenuButton = false,
  onMenuClick,
  showCollapseButton = false,
  onCollapseClick,
  isSidebarCollapsed = false,
  backgroundStyle = TopbarBackgroundStyle.DEFAULT,
  enableSearch = false,
  searchFields = [],
  onSearch,
  onSearchFieldChange,
  initialSearchQuery = '',
  initialSearchField,
  enableScan = false,
  enableUserLogoutSection = true,
  primaryAction,
  children,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Search state
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [selectedSearchField, setSelectedSearchField] = useState<SearchField>(() => {
    // Initialize with first available field or default
    return searchFields.find((field) => field.id === initialSearchField) || searchFields[0] || DEFAULT_SEARCH_FIELD;
  });
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  
  // Sync selectedSearchField with searchFields prop changes
  useEffect(() => {
    if (searchFields.length > 0) {
      // Check if current selectedSearchField exists in new searchFields
      const fieldExists = searchFields.some(field => field.id === selectedSearchField.id);
      if (!fieldExists) {
        // If current field doesn't exist, reset to first field
        setSelectedSearchField(searchFields[0]);
      }
    } else {
      // If no searchFields, only reset if not already the default
      if (selectedSearchField.id !== DEFAULT_SEARCH_FIELD.id) {
        setSelectedSearchField(DEFAULT_SEARCH_FIELD);
      }
    }
  }, [searchFields]);
  
  // User menu state
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  // Mobile search toggle state
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const handleLogout = async () => {
    try {
      if (window.confirm('คุณต้องการออกจากระบบใช่หรือไม่?')) {
        await logout();
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSearchFieldSelect = (field: SearchField) => {
    setSelectedSearchField(field);
    setIsSearchDropdownOpen(false);
    onSearchFieldChange?.(field.id);
    onSearch?.(searchQuery, field.id);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    onSearch?.(query, selectedSearchField.id);
  };

  const clearSearch = () => {
    setSearchQuery('');
    onSearch?.('', selectedSearchField.id);
  };

  // Get background style classes and inline styles
  const getBackgroundStyles = () => {
    switch (backgroundStyle) {
      case TopbarBackgroundStyle.BLUE_GRADIENT:
        return {
          className: "sticky top-0 z-10 w-full border-b border-blue-300/50 shadow-lg",
          style: { background: 'linear-gradient(90deg, #2563eb 0%, #1e40af 100%)' }
        };
      case TopbarBackgroundStyle.DARK:
        return {
          className: "sticky top-0 z-10 w-full border-b border-gray-700 bg-gray-900 shadow-sm",
          style: {}
        };
      case TopbarBackgroundStyle.TRANSPARENT:
        return {
          className: "sticky top-0 z-10 w-full border-b border-gray-200/30 bg-transparent backdrop-blur-xl shadow-sm",
          style: {}
        };
      case TopbarBackgroundStyle.WHITE:
        return {
          className: "sticky top-0 z-10 w-full border-b border-gray-200 bg-white shadow-sm",
          style: {}
        };
      case TopbarBackgroundStyle.YELLOW:
        return {
          className: "sticky top-0 z-10 w-full border-b-2 border-yellow-400 bg-white shadow-sm",
          style: {}
        };
      case TopbarBackgroundStyle.BLUE:
        return {
          className: "sticky top-0 z-10 w-full border-b-2 border-blue-500 bg-white shadow-sm",
          style: {}
        };
      case TopbarBackgroundStyle.GREEN:
        return {
          className: "sticky top-0 z-10 w-full border-b-2 border-green-500 bg-white shadow-sm",
          style: {}
        };
      case TopbarBackgroundStyle.RED:
        return {
          className: "sticky top-0 z-10 w-full border-b-2 border-rose-500 bg-white shadow-sm",
          style: {}
        };
      case TopbarBackgroundStyle.DEFAULT:
      default:
        return {
          className: "sticky top-0 z-10 w-full border-b border-gray-200/80 bg-white/95 backdrop-blur-xl supports-[backdrop-filter]:bg-white/85 shadow-sm",
          style: {}
        };
    }
  };

  // Get text color classes based on background style
  const getTextColorClasses = () => {
    switch (backgroundStyle) {
      case TopbarBackgroundStyle.BLUE_GRADIENT:
        return {
          title: "text-white",
          buttons: "text-white/80 hover:text-white hover:bg-white/10",
          menuButtons: "text-white/80 hover:text-white hover:bg-white/20"
        };
      case TopbarBackgroundStyle.DARK:
        return {
          title: "text-white",
          buttons: "text-gray-300 hover:text-white hover:bg-gray-800",
          menuButtons: "text-gray-300 hover:text-white hover:bg-gray-800"
        };
      default:
        return {
          title: "text-gray-900",
          buttons: "text-gray-500 hover:text-gray-700 hover:bg-gray-100",
          menuButtons: "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
        };
    }
  };

  const backgroundStyles = getBackgroundStyles();
  const textColors = getTextColorClasses();

  return (
    <header className={backgroundStyles.className} style={backgroundStyles.style}>
      <div className="container flex h-16 max-w-screen-2xl items-center justify-between px-3 sm:px-4 gap-4">    
        {/* Left section - Controls & Title */}
        <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
          {/* Mobile menu button */}
          {showMenuButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
              className={`lg:hidden p-2 transition-all duration-200 rounded-lg group ${textColors.menuButtons}`}
            >
              <Menu className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
            </Button>
          )}
          
          {/* Desktop sidebar collapse button */}
          {showCollapseButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onCollapseClick}
              className={`hidden lg:flex p-2 transition-all duration-200 rounded-lg group ${textColors.menuButtons}`}
            >
              {isSidebarCollapsed ? (
                <PanelLeftOpen className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
              ) : (
                <PanelLeftClose className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
              )}
            </Button>
          )}

          {children && <div className="flex items-center gap-0 sm:gap-2 min-w-0">
            {children}
          </div>}

          {/* Title */}
          {title && <div className="flex items-center gap-2 min-w-0">
            <h1 className={`text-xs sm:text-lg xl:text-2xl font-semibold truncate bg-gradient-to-r ${
              backgroundStyle === TopbarBackgroundStyle.BLUE_GRADIENT || backgroundStyle === TopbarBackgroundStyle.DARK 
                ? 'from-white to-gray-100 text-white' 
                : 'from-gray-900 to-gray-700 text-gray-900'
            } bg-clip-text`}>
              {title}
            </h1>
          </div>}
        </div>

        {/* Center section - Search (if enabled) */}
        {enableSearch && searchFields.length > 0 && (
          <div className="flex-1 max-w-4xl mx-4 hidden md:block">
            <div className="relative flex bg-gray-50/50 rounded-xl border border-gray-200/60 hover:shadow-md transition-all duration-200 hover:bg-gray-50/80">
              {/* Search Field Dropdown */}
              <Popover open={isSearchDropdownOpen} onOpenChange={setIsSearchDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    role="combobox"
                    aria-expanded={isSearchDropdownOpen}
                    className="h-11 px-4 bg-gradient-to-r from-gray-50 to-gray-100/50 border-r border-gray-200/60 hover:from-gray-100 hover:to-gray-150 transition-all duration-200 whitespace-nowrap rounded-l-xl border-0"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {selectedSearchField.label}
                    </span>
                    <ChevronDown size={14} className={`ml-2 text-gray-500 transition-all duration-200 ${isSearchDropdownOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-36 p-0" align="start">
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        {searchFields.map((field) => (
                          <CommandItem
                            key={field.id}
                            value={field.label}
                            onSelect={() => handleSearchFieldSelect(field)}
                            className="flex items-center gap-2"
                          >
                            <Check
                              className={cn(
                                "h-4 w-4",
                                selectedSearchField.id === field.id ? "text-blue-600 opacity-100" : "opacity-0"
                              )}
                            />
                            {field.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Search Input */}
              <div className="relative flex-1">
                <Search
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  value={searchQuery}
                  placeholder={selectedSearchField.placeholder}
                  className={`w-full h-11 pl-12 pr-12 bg-transparent focus:outline-none focus:ring-0 text-sm text-gray-700 placeholder:text-gray-400 ${!enableScan ? 'rounded-r-xl' : ''}`}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Enhanced Scan button */}
              {enableScan && (
                <div className="flex items-center border-l border-gray-200/60">
                  <button
                    onClick={() => navigate('/scan')}
                    className="h-11 px-3 lg:px-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium transition-all duration-200 flex items-center justify-center gap-2 group relative overflow-hidden rounded-r-xl"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <ScanLine className="h-4 w-4 relative z-10 group-hover:scale-110 transition-transform duration-200" />
                    <span className="text-sm relative z-10 hidden lg:inline">สแกน</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right section - Actions & User */}
        {enableUserLogoutSection && <div className="flex items-center gap-1 flex-shrink-0">
          {/* Primary Action Button */}
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              className={`hidden sm:flex ${
                primaryAction.variant === 'secondary' 
                  ? 'bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-full shadow-md hover:bg-gray-50 hover:border-gray-400' 
                  : 'bg-blue-600 text-white px-4 py-2 rounded-full shadow-md hover:bg-blue-700'
              }`}
            >
              {primaryAction.label}
            </button>
          )}

          {/* Notifications */}
          <NotificationDropdown 
            className={textColors.buttons}
          />

          {/* User Menu */}
          {user ? (
            <div>
              <Popover open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
                <PopoverTrigger asChild>
                <button
                  className={`flex items-center gap-1 sm:gap-3 px-0 sm:px-3 py-2 rounded-lg transition-all duration-200 group border border-transparent ${
                    backgroundStyle === TopbarBackgroundStyle.BLUE_GRADIENT || backgroundStyle === TopbarBackgroundStyle.DARK
                      ? 'hover:bg-white/10 hover:border-white/20'
                      : 'hover:bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <UserProfileImage 
                    currentAssignee={{
                      id: user.user_id,
                      username: user.full_name,
                      isActive: true,
                      roleId: 0,
                      createdAt: '',
                      updatedAt: '',
                      name: user.full_name
                    }} 
                    size="sm" 
                  />
                  <div className="hidden sm:flex flex-col text-left">
                    <span className={`text-sm font-medium leading-tight group-hover:opacity-80 transition-opacity ${textColors.title}`}>
                      {user.full_name}
                    </span>
                    <span className={`text-xs capitalize group-hover:opacity-80 transition-opacity ${
                      backgroundStyle === TopbarBackgroundStyle.BLUE_GRADIENT || backgroundStyle === TopbarBackgroundStyle.DARK
                        ? 'text-white/70'
                        : 'text-gray-500'
                    }`}>
                      {user.role.toLowerCase()}
                    </span>
                  </div>
                  <ChevronDown className={`h-3 w-3 transition-all duration-300 ${
                    backgroundStyle === TopbarBackgroundStyle.BLUE_GRADIENT || backgroundStyle === TopbarBackgroundStyle.DARK
                      ? 'text-white/80 group-hover:text-white'
                      : 'text-gray-500 group-hover:text-gray-700'
                  } ${isUserMenuOpen ? 'rotate-180 text-blue-500' : ''}`} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="end">
                <div className="px-4 py-3 border-b border-gray-100/80">
                  <div className="flex items-center gap-3">
                    <UserProfileImage 
                      currentAssignee={{
                        id: user.user_id,
                        username: user.full_name,
                        isActive: true,
                        roleId: 0,
                        createdAt: '',
                        updatedAt: '',
                        name: user.full_name
                      }} 
                      size="sm" 
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                      <div className="text-xs text-gray-500 capitalize flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        {user.role.toLowerCase()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="py-2">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/profile');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-150"
                  >
                    <User className="h-4 w-4" />
                    <span className="font-medium">โปรไฟล์ของฉัน</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all duration-150 group"
                  >
                    <LogOut className="h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-medium">ออกจากระบบ</span>
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-2 py-1.5">
              <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse"></div>
              <div className="hidden sm:flex flex-col gap-1">
                <div className="h-3 w-16 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          )}
        </div>}
      </div>

      {/* Collapsible Mobile Search */}
      {enableSearch && searchFields.length > 0 && (
        <div className="md:hidden border-t border-gray-200/60 bg-gray-50/30">
          {/* Mobile Search Toggle Button */}
          <div className="flex items-center justify-between px-3 py-2">
            <button
              onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <Search className="h-4 w-4" />
              <span>ค้นหา</span>
              <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isMobileSearchOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Action buttons when search is closed */}
            {!isMobileSearchOpen && (
              <div className="flex items-center gap-2">
                {primaryAction && (
                  <button
                    onClick={primaryAction.onClick}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                      primaryAction.variant === 'secondary' 
                        ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    } transition-colors duration-200`}
                  >
                    {primaryAction.label}
                  </button>
                )}
                
                {enableScan && (
                  <button
                    onClick={() => navigate('/scan')}
                    className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition-all duration-200 group"
                  >
                    <ScanLine className="h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Expandable Search Section */}
          {isMobileSearchOpen && (
            <div className="px-3 pb-3 space-y-2">
              {/* Primary Action in expanded view */}
              {primaryAction && (
                <button
                  onClick={primaryAction.onClick}
                  className={`w-full px-4 py-2.5 text-sm font-medium rounded-lg ${
                    primaryAction.variant === 'secondary' 
                      ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  } transition-colors duration-200`}
                >
                  {primaryAction.label}
                </button>
              )}

              {/* Search Field Selector */}
              <Popover open={isSearchDropdownOpen} onOpenChange={setIsSearchDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={isSearchDropdownOpen}
                    className="w-full h-10 px-3 justify-between"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {selectedSearchField.label}
                    </span>
                    <ChevronDown size={14} className={`text-gray-500 transition-transform duration-200 ${isSearchDropdownOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        {searchFields.map((field) => (
                          <CommandItem
                            key={field.id}
                            value={field.label}
                            onSelect={() => handleSearchFieldSelect(field)}
                            className="flex items-center gap-2"
                          >
                            <Check
                              className={cn(
                                "h-4 w-4",
                                selectedSearchField.id === field.id ? "text-blue-600 opacity-100" : "opacity-0"
                              )}
                            />
                            {field.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Search Input with Scan Button */}
              <div className="relative flex bg-white rounded-lg border border-gray-200/60 overflow-hidden">
                <div className="relative flex-1">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={16}
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    placeholder={selectedSearchField.placeholder}
                    className="w-full h-10 pl-10 pr-10 bg-transparent focus:outline-none focus:ring-0 text-sm text-gray-700 placeholder:text-gray-400"
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded-full hover:bg-gray-100"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {enableScan && (
                  <div className="flex items-center border-l border-gray-200/60">
                    <button
                      onClick={() => navigate('/scan')}
                      className="h-10 w-12 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white transition-all duration-200 flex items-center justify-center group"
                    >
                      <ScanLine className="h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default EnhancedModernTopbar;
